/**
 * Build LDS split-payment card rows from server payments[] — immutable locked slots.
 */
import { runPaymentInstallmentPricing } from "../../pricing/paymentInstallmentsPricing";
import { compactPricingAuditMeta } from "../../pricing/compactPricingAuditMeta";
import { round2 } from "../../contexts/AppContext";
import {
  getLdsSplitPaymentSlotCapturedIls,
  isLdsSplitPaymentSlotLocked,
} from "./ldsSplitPaymentSlotLock.js";
import { resolveLdsFinancialState } from "./resolveLdsFinancialState.js";
import {
  parseLdsOrderPayments,
  resolveLdsPaymentSlotCapturedStrict,
  sumLdsPaymentsCapturedStrict,
  traceLdsSplitPaymentsSsot,
} from "./ldsSplitPaymentsSsot.js";

export const LDS_EMPTY_CARD_TEMPLATE = {
  numberofpayments: 1,
  paymentsInterest: 0,
  amount: 0,
  amountWithInterest: 0,
  paid: false,
  ldsSlotLocked: false,
};

function toNum(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function applyInstallmentPricing(card, interestTable, pricingAuditMeta) {
  const newCard = { ...card };
  newCard.amount = parseFloat(newCard.amount) || 0;
  const priced = runPaymentInstallmentPricing({
    principalIls: newCard.amount,
    numberOfPayments: newCard.numberofpayments,
    interestTable,
    pricingAuditMeta: compactPricingAuditMeta({
      ...(pricingAuditMeta && typeof pricingAuditMeta === "object"
        ? pricingAuditMeta
        : {}),
      installmentsSelected: Math.max(
        1,
        Math.floor(Number(newCard.numberofpayments)) || 1
      ),
    }),
  });
  newCard.paymentInterest = priced.paymentInterest;
  newCard.paymentsInterest = priced.paymentInterest;
  newCard.amountWithInterest = priced.amountWithInterest;
  newCard.paymentAction = priced.paymentAction;
  newCard.paymentPricingAuditSteps = priced.auditSteps;
  return newCard;
}

/** Sum captured/paid ILS from payments[] (SSOT — direct per-slot fields only). */
export function sumLdsPaymentsCapturedIls(payments, order = null) {
  const list = Array.isArray(payments) ? payments : parseLdsOrderPayments(order);
  return sumLdsPaymentsCapturedStrict(list);
}

export function ldsSplitOrderCardsFingerprint(order) {
  if (!order || typeof order !== "object") return "";
  const payments = Array.isArray(order.payments) ? order.payments : [];
  const fin = resolveLdsFinancialState(order);
  return JSON.stringify({
    id: order.id ?? order._id ?? "",
    sumpaid: order.sumpaid,
    remaining: fin.remainingBalance,
    isPartialPaid: fin.isPartialPaid,
    payments: payments.map((p, i) => ({
      i,
      paid: p?.paid,
      j5: p?.j5_capture_state,
      cap: p?.cumulative_captured_ils ?? p?.captured_amount_ils,
      aa: p?.actualAmount,
      a: p?.amount,
      t: p?.tash,
      url: Boolean(p?.url),
    })),
  });
}

/**
 * @param {object|null} order
 * @param {number} remainingBalance — payable remaining (from PaymentAgent full_price)
 * @param {*} interestTable
 * @param {object} pricingAuditMeta
 */
export function buildLdsSplitCardsFromOrder(
  order,
  remainingBalance,
  interestTable,
  pricingAuditMeta
) {
  const payments = parseLdsOrderPayments(order);
  const fin = resolveLdsFinancialState(order);
  const payableRemaining =
    fin.remainingBalance != null
      ? round2(Math.max(fin.remainingBalance, 0))
      : remainingBalance != null && Number.isFinite(Number(remainingBalance))
        ? round2(Math.max(Number(remainingBalance), 0))
        : 0;
  // Prefill in whole shekels (ceil) — mirrors the BE payable cap
  // (round_up_lds_payment_remaining), which allows ceil(remaining) explicitly.
  const payableRemainingWholeIls =
    payableRemaining > 0.02 ? Math.ceil(payableRemaining - 1e-9) : payableRemaining;

  const cards = [];

  for (let i = 0; i < payments.length; i++) {
    const p = payments[i];
    const locked = isLdsSplitPaymentSlotLocked(p, order, i);
    const captured = resolveLdsPaymentSlotCapturedStrict(p, order, i);
    const tash = Number(p.tash) || 1;

    if (locked) {
      cards.push(
        applyInstallmentPricing(
          {
            ...LDS_EMPTY_CARD_TEMPLATE,
            numberofpayments: tash,
            amount: captured,
            amountWithInterest: toNum(p.amount) || captured,
            paid: true,
            ldsSlotLocked: true,
            ldsPaymentSlotIndex: i,
          },
          interestTable,
          pricingAuditMeta
        )
      );
    } else {
      const amt = toNum(p.actualAmount ?? p.actual_amount ?? p.amount);
      cards.push(
        applyInstallmentPricing(
          {
            ...LDS_EMPTY_CARD_TEMPLATE,
            numberofpayments: tash,
            amount: amt,
            amountWithInterest: toNum(p.amount) || amt,
            paid: Boolean(p.paid),
            ldsSlotLocked: false,
            ldsPaymentSlotIndex: i,
          },
          interestTable,
          pricingAuditMeta
        )
      );
    }
  }

  if (payableRemaining > 0.02) {
    const syntheticIdx = cards.findIndex(
      (c, i) =>
        i >= payments.length &&
        isLdsSplitPaymentCardEditable(c, payments[i], order, i)
    );

    if (syntheticIdx < 0) {
      cards.push(
        applyInstallmentPricing(
          {
            ...LDS_EMPTY_CARD_TEMPLATE,
            amount: payableRemainingWholeIls,
            numberofpayments: 1,
            paid: false,
            ldsSlotLocked: false,
            ldsPaymentSlotIndex: payments.length,
          },
          interestTable,
          pricingAuditMeta
        )
      );
    } else {
      cards[syntheticIdx] = applyInstallmentPricing(
        { ...cards[syntheticIdx], amount: payableRemainingWholeIls },
        interestTable,
        pricingAuditMeta
      );
    }
  }

  if (cards.length === 0 && payableRemaining > 0) {
    cards.push(
      applyInstallmentPricing(
        {
          ...LDS_EMPTY_CARD_TEMPLATE,
          amount: payableRemainingWholeIls,
          numberofpayments: 1,
        },
        interestTable,
        pricingAuditMeta
      )
    );
  }

  return cards;
}

/**
 * After a J5/partial capture rebuild, keep agent-typed amounts + installments on
 * unlocked cards instead of resetting the next free slot to full remaining / tash=1.
 *
 * Server-backed slots with a payment URL keep the linked amount (already issued).
 * Extra unlocked cards the agent added (card count > built synthetics) are appended.
 *
 * @param {object[]} built — from buildLdsSplitCardsFromOrder
 * @param {object[]|null|undefined} previous — current UI cards before rebuild
 * @param {object|null} order
 * @param {*} interestTable
 * @param {object} pricingAuditMeta
 */
export function mergeLdsSplitCardsAgentPrefs(
  built,
  previous,
  order,
  interestTable,
  pricingAuditMeta
) {
  if (!Array.isArray(built) || built.length === 0) return built;
  if (!Array.isArray(previous) || previous.length === 0) return built;

  const payments = parseLdsOrderPayments(order);
  const result = built.map((c) => ({ ...c }));

  const paymentHasLink = (index) => {
    const p = payments[index];
    return Boolean(p && String(p.url || p.paymentUrl || p.payment_url || "").trim());
  };

  const normalizeTash = (value) =>
    Math.max(1, Math.floor(Number(value)) || 1);

  for (let i = 0; i < result.length; i++) {
    if (!isLdsSplitPaymentCardEditable(result[i], payments[i], order, i)) {
      continue;
    }
    // Issued link amount is authoritative until the agent creates a new link.
    if (paymentHasLink(i)) continue;

    const prevCard = previous[i];
    if (!prevCard || prevCard.ldsSlotLocked === true || prevCard.paid === true) {
      continue;
    }

    const prevAmt = Number(prevCard.amount);
    const prevTash = normalizeTash(prevCard.numberofpayments);
    const hasPrevAmount = Number.isFinite(prevAmt) && prevAmt > 0;
    const builtTash = normalizeTash(result[i].numberofpayments);

    if (!hasPrevAmount && prevTash <= 1) continue;
    if (
      hasPrevAmount &&
      round2(prevAmt) === round2(Number(result[i].amount) || 0) &&
      prevTash === builtTash
    ) {
      continue;
    }

    result[i] = applyInstallmentPricing(
      {
        ...result[i],
        amount: hasPrevAmount ? prevAmt : result[i].amount,
        numberofpayments: prevTash,
      },
      interestTable,
      pricingAuditMeta
    );
  }

  // Preserve additional unlocked cards the agent selected (e.g. 3 cards) when
  // rebuild only synthesizes locked slots + one full-remaining card.
  for (let i = result.length; i < previous.length; i++) {
    const prevCard = previous[i];
    if (!prevCard || prevCard.ldsSlotLocked === true || prevCard.paid === true) {
      continue;
    }
    if (
      i < payments.length &&
      isLdsSplitPaymentSlotLocked(payments[i], order, i)
    ) {
      continue;
    }

    const prevAmt = Number(prevCard.amount);
    const amount = Number.isFinite(prevAmt) && prevAmt > 0 ? prevAmt : 0;
    const numberofpayments = normalizeTash(prevCard.numberofpayments);

    result.push(
      applyInstallmentPricing(
        {
          ...LDS_EMPTY_CARD_TEMPLATE,
          amount,
          numberofpayments,
          paid: false,
          ldsSlotLocked: false,
          ldsPaymentSlotIndex: i,
        },
        interestTable,
        pricingAuditMeta
      )
    );
  }

  return result;
}

/** @returns {boolean} true when card may receive edits / remaining balance */
export function isLdsSplitPaymentCardEditable(card, payment, order, index) {
  if (!card || typeof card !== "object") return false;
  if (card.ldsSlotLocked === true) return false;
  if (card.paid === true) return false;
  if (payment && isLdsSplitPaymentSlotLocked(payment, order, index)) return false;
  return true;
}

/**
 * First card index safe for edits / remaining balance routing.
 * Skips locked slots, paid cards, and server-locked payment slots.
 */
export function findFirstUnlockedLdsCardIndex(cards, order = null) {
  if (!Array.isArray(cards)) return -1;
  const payments = Array.isArray(order?.payments) ? order.payments : null;
  for (let i = 0; i < cards.length; i++) {
    const payment = payments?.[i] ?? null;
    if (isLdsSplitPaymentCardEditable(cards[i], payment, order, i)) {
      return i;
    }
  }
  return -1;
}

/**
 * Resolve active card index — never returns a locked slot.
 * @param {number} selectedIndex
 */
export function resolveLdsActiveCardIndex(cards, selectedIndex, order = null) {
  const unlocked = findFirstUnlockedLdsCardIndex(cards, order);
  if (unlocked < 0) return -1;
  const idx = Number(selectedIndex);
  if (
    Number.isFinite(idx) &&
    idx >= 0 &&
    idx < cards.length &&
    isLdsSplitPaymentCardEditable(
      cards[idx],
      order?.payments?.[idx] ?? null,
      order,
      idx
    )
  ) {
    return idx;
  }
  return unlocked;
}

/** @returns {number|null} Display amount for locked card header — server capture only */
export function resolveLdsLockedCardDisplayAmount(payment, order, cardIndex) {
  if (payment && typeof payment === "object") {
    const strict = resolveLdsPaymentSlotCapturedStrict(payment, order, cardIndex);
    if (strict > 0) return strict;
  }
  return getLdsSplitPaymentSlotCapturedIls(payment, order, cardIndex);
}

export { traceLdsSplitPaymentsSsot };
